"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { BookOpen, List, Lock, Sparkles, Waypoints } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MobileAssistantSheet } from "@/components/editor/mobile-assistant-sheet";
import { DemoChapterList } from "./demo-chapter-list";
import type { GateReason } from "./demo-gate-modal";

/**
 * Mobile / tablet bottom bar for the `/try` demo. Hidden at `lg`+, where the
 * chapter list and assistant show inline. Below `lg` those panels are hidden,
 * so this bar is the only way to reach them — it surfaces the four product
 * areas as thumb targets, mirroring the desktop header:
 *
 *   • Chapters  — opens the chapter list as a left drawer
 *   • Assistant — slides the AI writing assistant up as a near-full sheet
 *                 (the demo's headline feature — must be reachable on phones)
 *   • Wiki       — opens the temporary client-only Wiki
 *   • Plot       — locked teaser that opens the sign-up gate
 *
 * The assistant chat is keyed by storyId:chapterId, so it's the same
 * conversation as the desktop inline panel.
 */
export function DemoMobileBar(t0) {
  const $ = _c(34);
  const {
    storyId,
    activeChapterId,
    onOpenGate,
    workspace: t1,
    onOpenEditor: t2,
    onOpenWiki: t3
  } = t0;
  const workspace = t1 === undefined ? "editor" : t1;
  const onOpenEditor = t2 === undefined ? _temp : t2;
  const onOpenWiki = t3 === undefined ? _temp2 : t3;
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  let t4;
  if ($[0] !== onOpenEditor || $[1] !== workspace) {
    t4 = workspace === "editor" ? <BarButton onClick={() => setChaptersOpen(true)}><List className="size-5" aria-hidden={true} />Chapters</BarButton> : <BarButton onClick={onOpenEditor}><BookOpen className="size-5" aria-hidden={true} />Editor</BarButton>;
    $[0] = onOpenEditor;
    $[1] = workspace;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== onOpenEditor || $[4] !== workspace) {
    t5 = () => {
      if (workspace === "wiki") {
        onOpenEditor();
      }
      setAssistantOpen(true);
    };
    $[3] = onOpenEditor;
    $[4] = workspace;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Sparkles className="size-5 text-primary" aria-hidden={true} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <BarButton onClick={t5}>{t6}Assistant</BarButton>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Waypoints className="size-5" aria-hidden={true} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== onOpenWiki) {
    t9 = <BarButton onClick={onOpenWiki}>{t8}Wiki</BarButton>;
    $[10] = onOpenWiki;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== onOpenGate) {
    t10 = () => onOpenGate("plot");
    $[12] = onOpenGate;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Lock className="size-5" aria-hidden={true} />;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10) {
    t12 = <BarButton onClick={t10}>{t11}Plot</BarButton>;
    $[15] = t10;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t4 || $[19] !== t7 || $[20] !== t9) {
    t13 = <nav className="flex shrink-0 items-stretch border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden" aria-label="Demo tools">{t4}{t7}{t9}{t12}</nav>;
    $[17] = t12;
    $[18] = t4;
    $[19] = t7;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SheetHeader className="sr-only"><SheetTitle>Chapters</SheetTitle><SheetDescription>Switch between chapters or add a new one.</SheetDescription></SheetHeader>;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SheetContent side="left" className="w-[280px] gap-0 p-0 sm:max-w-[280px]">{t14}<DemoChapterList onNavigate={() => setChaptersOpen(false)} showCount={false} /></SheetContent>;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== chaptersOpen) {
    t16 = <Sheet open={chaptersOpen} onOpenChange={setChaptersOpen}>{t15}</Sheet>;
    $[24] = chaptersOpen;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== activeChapterId || $[27] !== assistantOpen || $[28] !== storyId) {
    t17 = <MobileAssistantSheet open={assistantOpen} onOpenChange={setAssistantOpen} storyId={storyId} chapterId={activeChapterId} />;
    $[26] = activeChapterId;
    $[27] = assistantOpen;
    $[28] = storyId;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t13 || $[31] !== t16 || $[32] !== t17) {
    t18 = <>{t13}{t16}{t17}</>;
    $[30] = t13;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  return t18;
}
function _temp2() {}
function _temp() {}
function BarButton(t0) {
  const $ = _c(3);
  const {
    onClick,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== onClick) {
    t1 = <button type="button" onClick={onClick} aria-haspopup="dialog" className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground active:bg-muted/50 [&:not(:first-child)]:border-l [&:not(:first-child)]:border-border">{children}</button>;
    $[0] = children;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}